import { z } from "zod";

export const CREDENTIAL_RECOVERY_WINDOW_MS = 15 * 60_000;

const apiKeyRecoverySchema = z
  .object({
    id: z.string().min(1),
    kind: z.literal("api_key"),
    organizationId: z.string().min(1),
    operationId: z.string().min(8).max(128),
    userId: z.string().min(1),
    name: z.string().min(1),
    agentName: z.string().nullable(),
    recoverableUntil: z.string().datetime(),
  })
  .strict();

const guestLinkRecoverySchema = z
  .object({
    id: z.string().min(1),
    kind: z.literal("guest_link"),
    organizationId: z.string().min(1),
    operationId: z.string().min(8).max(128),
    userId: z.string().min(1),
    siteId: z.string().min(1),
    siteName: z.string().min(1),
    expiresAt: z.string().datetime().nullable().default(null),
    recoverableUntil: z.string().datetime(),
  })
  .strict();

const credentialRecoveriesSchema = z.array(
  z.discriminatedUnion("kind", [apiKeyRecoverySchema, guestLinkRecoverySchema]),
);

export type ApiKeyCredentialRecovery = z.infer<typeof apiKeyRecoverySchema>;
export type GuestLinkCredentialRecovery = z.infer<
  typeof guestLinkRecoverySchema
>;
export type CredentialRecovery = z.infer<
  typeof credentialRecoveriesSchema
>[number];

export interface CredentialRecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORAGE_KEY = "appnotes:credential-recoveries:v1";
const MAX_RECOVERY_RECORDS = 20;

/** Persist non-secret receipts that can resume interrupted credential issuance. */
export class CredentialRecoveryStore {
  readonly #now: () => number;
  readonly #storage: CredentialRecoveryStorage | null;
  #records: readonly CredentialRecovery[] | null = null;

  public constructor(
    storage: CredentialRecoveryStorage | null,
    now: () => number = Date.now,
  ) {
    this.#storage = storage;
    this.#now = now;
  }

  public list(
    organizationId: string,
    userId: string,
  ): readonly CredentialRecovery[] {
    const current = this.#prune(this.#load());
    return current.filter(
      (recovery) =>
        recovery.organizationId === organizationId &&
        recovery.userId === userId,
    );
  }

  public beginApiKey(input: {
    readonly agentName: string | null;
    readonly name: string;
    readonly operationId: string;
    readonly organizationId: string;
    readonly userId: string;
  }): ApiKeyCredentialRecovery {
    return this.#save({
      id: `api_key:${input.organizationId}:${input.operationId}`,
      kind: "api_key",
      organizationId: input.organizationId,
      operationId: input.operationId,
      userId: input.userId,
      name: input.name,
      agentName: input.agentName,
      recoverableUntil: new Date(
        this.#now() + CREDENTIAL_RECOVERY_WINDOW_MS,
      ).toISOString(),
    });
  }

  public beginGuestLink(input: {
    readonly expiresAt?: string | null;
    readonly operationId: string;
    readonly organizationId: string;
    readonly siteId: string;
    readonly siteName: string;
    readonly userId: string;
  }): GuestLinkCredentialRecovery {
    return this.#save({
      id: `guest_link:${input.siteId}:${input.operationId}`,
      kind: "guest_link",
      organizationId: input.organizationId,
      operationId: input.operationId,
      userId: input.userId,
      siteId: input.siteId,
      siteName: input.siteName,
      expiresAt: input.expiresAt ?? null,
      recoverableUntil: new Date(
        this.#now() + CREDENTIAL_RECOVERY_WINDOW_MS,
      ).toISOString(),
    });
  }

  public setDeadline(
    recovery: CredentialRecovery,
    recoverableUntil: string,
  ): CredentialRecovery {
    const updated = { ...recovery, recoverableUntil };
    return this.#save(updated);
  }

  public remove(id: string): void {
    this.#persist(this.#load().filter((recovery) => recovery.id !== id));
  }

  #save<T extends CredentialRecovery>(recovery: T): T {
    const withoutCurrent = this.#prune(this.#load()).filter(
      (current) => current.id !== recovery.id,
    );
    this.#persist([...withoutCurrent, recovery].slice(-MAX_RECOVERY_RECORDS));
    return recovery;
  }

  #load(): readonly CredentialRecovery[] {
    if (this.#records !== null) return this.#records;
    if (this.#storage === null) {
      this.#records = [];
      return this.#records;
    }
    try {
      const raw = this.#storage.getItem(STORAGE_KEY);
      if (raw === null) {
        this.#records = [];
        return this.#records;
      }
      const parsed = credentialRecoveriesSchema.safeParse(JSON.parse(raw));
      this.#records = parsed.success ? parsed.data : [];
    } catch {
      this.#records = [];
    }
    return this.#records;
  }

  #prune(
    records: readonly CredentialRecovery[],
  ): readonly CredentialRecovery[] {
    const current = records.filter(
      (recovery) => Date.parse(recovery.recoverableUntil) > this.#now(),
    );
    if (current.length !== records.length) this.#persist(current);
    return current;
  }

  #persist(records: readonly CredentialRecovery[]): void {
    this.#records = records;
    if (this.#storage === null) return;
    try {
      this.#storage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch {
      // Browser storage can be unavailable; retain the receipt in memory.
    }
  }
}

/** Create a recovery store without assuming that browser storage is available. */
export function createBrowserCredentialRecoveryStore(): CredentialRecoveryStore {
  try {
    return new CredentialRecoveryStore(window.localStorage);
  } catch {
    return new CredentialRecoveryStore(null);
  }
}
